import { useMemo } from 'react';

import {
    AviationDocumentCategory,
    CategoryComplianceStats,
} from '@/features/end-of-lease/types';

import { IATA_BINDER_DISPLAY_MAP } from '@/lib/constants';
import { formatDate } from '@/lib/utils';

// Mock data for demo purposes - shows realistic folder stats
// when no real compliance data is available
const MOCK_FOLDER_STATS: Record<
    AviationDocumentCategory,
    { documents: number; progress: number; lastUpdate: string }
> = {
    Certificates: { documents: 14, progress: 100, lastUpdate: '2 days ago' },
    MaintenanceStatusSummaries: {
        documents: 38,
        progress: 92,
        lastUpdate: '1 day ago',
    },
    AircraftMaintenanceRecords: {
        documents: 12,
        progress: 51,
        lastUpdate: '3 hours ago',
    },
    ConfigurationStatus: {
        documents: 23,
        progress: 100,
        lastUpdate: '5 days ago',
    },
    AircraftManufacturerRecords: {
        documents: 51,
        progress: 100,
        lastUpdate: '1 week ago',
    },
    EngineRecords_1: {
        documents: 5,
        progress: 100,
        lastUpdate: '30 mins ago',
    },
    EngineRecords_2: {
        documents: 5,
        progress: 60,
        lastUpdate: '15 mins ago',
    },
    APURecords: { documents: 42, progress: 100, lastUpdate: '6 days ago' },
    ComponentRecords: {
        documents: 1001,
        progress: 40,
        lastUpdate: '1 hour ago',
    },
    LandingGearRecords: {
        documents: 76,
        progress: 100,
        lastUpdate: '3 days ago',
    },
    Manuals: { documents: 19, progress: 100, lastUpdate: '2 weeks ago' },
    Miscellaneous: { documents: 3, progress: 0, lastUpdate: 'No activity' },
};

export type IATAFolderData = {
    letter: string;
    name: string;
    progress: number;
    status: 'complete' | 'in-progress';
    assignee: string;
    priority: 'high' | 'medium' | 'low';
    documents: number;
    lastUpdate: string;
    complianceDetails?: {
        totalChecks: number;
        passedChecks: number;
        failedChecks: number;
        totalItems: number;
    };
};

function createFolderData(
    category: AviationDocumentCategory,
    complianceSummary?: CategoryComplianceStats[],
): IATAFolderData {
    const { letter, name, assignee } = IATA_BINDER_DISPLAY_MAP[category];

    const complianceData = complianceSummary?.find(
        (item) => item.category === category,
    );

    // Use real compliance data only if we have actual data with items
    // Otherwise fall back to mock data for demo purposes
    // For ComponentRecords, always use mock data for demo
    const mockData = MOCK_FOLDER_STATS[category];
    const hasRealData =
        complianceData !== undefined &&
        complianceData.totalItems > 0 &&
        category !== 'ComponentRecords';

    const progress = hasRealData
        ? complianceData.progressPercentage || 0
        : mockData.progress;
    const status = progress >= 100 ? 'complete' : 'in-progress';
    const lastUpdate = hasRealData
        ? complianceData.lastUpdated
            ? formatDate(complianceData.lastUpdated)
            : 'No activity'
        : mockData.lastUpdate;
    const documents = hasRealData
        ? complianceData.totalItems || 0
        : mockData.documents;

    // Priority for display: show as 'high' (red) only if progress < 100
    // Otherwise show as 'low' (green) to indicate completion
    const displayPriority: 'high' | 'medium' | 'low' =
        progress > 0 && progress < 100 ? 'high' : 'low';

    return {
        letter,
        name,
        progress,
        status,
        assignee,
        priority: displayPriority,
        documents,
        lastUpdate,
        complianceDetails: complianceData
            ? {
                  totalChecks: complianceData.totalChecks,
                  passedChecks: complianceData.passedChecks,
                  failedChecks: complianceData.failedChecks,
                  totalItems: complianceData.totalItems,
              }
            : undefined,
    };
}

function getCategoryFromLetter(
    letter: string,
): AviationDocumentCategory | undefined {
    return Object.entries(IATA_BINDER_DISPLAY_MAP).find(
        ([_, value]) => value.letter === letter,
    )?.[0] as AviationDocumentCategory | undefined;
}

export function useIATAFolderByLetter(
    letter: string,
    complianceSummary?: CategoryComplianceStats[],
): IATAFolderData | undefined {
    const category = getCategoryFromLetter(letter);

    return useMemo(() => {
        if (!category) return undefined;
        return createFolderData(category, complianceSummary);
    }, [category, complianceSummary]);
}

export function useIATAFolders(
    categories: AviationDocumentCategory[],
    complianceSummary?: CategoryComplianceStats[],
): IATAFolderData[] {
    return useMemo(() => {
        return categories.map((category) =>
            createFolderData(category, complianceSummary),
        );
    }, [categories, complianceSummary]);
}
